import { nanoid } from 'nanoid';
import { StateStore } from 'stream-chat';

export type GetDialogParams = {
  id: DialogId;
};
export type GetOrCreateDialogParams = GetDialogParams & {
  /**
   * Optional per-dialog override.
   * If undefined, manager-level `closeOnClickOutside` is used.
   */
  closeOnClickOutside?: boolean;
};

type DialogId = string;

export type Dialog = {
  close: () => void;
  closeOnClickOutside?: boolean;
  id: DialogId;
  isOpen: boolean | undefined;
  open: (zIndex?: number) => void;
  removalTimeout: NodeJS.Timeout | undefined;
  remove: () => void;
  toggle: (closeAll?: boolean) => void;
};

export type DialogManagerOptions = {
  /** Enables closing all dialogs in this manager when clicking overlay/outside area. */
  closeOnClickOutside?: boolean;
  id?: string;
};

type Dialogs = Record<DialogId, Dialog>;

export type DialogManagerState = {
  dialogsById: Dialogs;
  openedDialogIds: DialogId[];
};

/**
 * Keeps a map of Dialog objects.
 * Dialog can be controlled via `Dialog` object retrieved using `useDialog()` hook.
 * The hook returns an object with the following API:
 *
 * - `dialog.open()` - opens the dialog
 * - `dialog.close()` - closes the dialog
 * - `dialog.toggle()` - toggles the dialog open state. Accepts boolean argument closeAll. If enabled closes any other dialog that would be open.
 * - `dialog.remove()` - removes the dialog object reference from the state (primarily for cleanup purposes)
 */
export class DialogManager {
  /** Manager-level outside click policy used by DialogPortal dismiss handlers. */
  closeOnClickOutside: boolean;
  id: string;
  state = new StateStore<DialogManagerState>({
    dialogsById: {},
    openedDialogIds: [],
  });

  constructor({ closeOnClickOutside = true, id }: DialogManagerOptions = {}) {
    this.closeOnClickOutside = closeOnClickOutside;
    this.id = id ?? nanoid();
  }

  get openDialogCount() {
    return this.state.getLatestValue().openedDialogIds.length;
  }

  get(id: DialogId): Dialog | undefined {
    return this.state.getLatestValue().dialogsById[id];
  }

  getOrCreate({ closeOnClickOutside, id }: GetOrCreateDialogParams) {
    let dialog = this.get(id);
    if (!dialog) {
      dialog = {
        close: () => {
          this.close(id);
        },
        closeOnClickOutside,
        id,
        isOpen: false,
        open: () => {
          this.open({ id });
        },
        removalTimeout: undefined,
        remove: () => {
          this.remove(id);
        },
        toggle: (closeAll = false) => {
          this.toggle({ id }, closeAll);
        },
      };
      this.state.next((current) => ({
        ...current,
        dialogsById: { ...current.dialogsById, [id]: dialog as Dialog },
      }));
    }

    const shouldUpdateDialogSettings =
      dialog.closeOnClickOutside !== closeOnClickOutside || !!dialog.removalTimeout;

    if (shouldUpdateDialogSettings) {
      if (dialog.removalTimeout) clearTimeout(dialog.removalTimeout);
      this.state.next((current) => ({
        ...current,
        dialogsById: {
          ...current.dialogsById,
          [id]: {
            ...current.dialogsById[id],
            closeOnClickOutside,
            removalTimeout: undefined,
          },
        },
        openedDialogIds: current.dialogsById[id]?.isOpen
          ? [...current.openedDialogIds.filter((dialogId) => dialogId !== id), id]
          : current.openedDialogIds,
      }));
    }

    return dialog;
  }

  open(params: GetOrCreateDialogParams, closeRest?: boolean) {
    const dialog = this.getOrCreate(params);
    if (dialog.isOpen) return;
    if (closeRest) {
      this.closeAll();
    }
    this.state.next((current) => ({
      ...current,
      dialogsById: { ...current.dialogsById, [dialog.id]: { ...dialog, isOpen: true } },
      openedDialogIds: [
        ...current.openedDialogIds.filter((dialogId) => dialogId !== dialog.id),
        dialog.id,
      ],
    }));
  }

  close(id: DialogId) {
    const dialog = this.state.getLatestValue().dialogsById[id];
    if (!dialog?.isOpen) return;
    this.state.next((current) => ({
      ...current,
      dialogsById: { ...current.dialogsById, [dialog.id]: { ...dialog, isOpen: false } },
      openedDialogIds: current.openedDialogIds.filter((dialogId) => dialogId !== id),
    }));
  }

  closeAll() {
    Object.values(this.state.getLatestValue().dialogsById).forEach((dialog) =>
      dialog.close(),
    );
  }

  toggle(params: GetOrCreateDialogParams, closeAll = false) {
    if (this.state.getLatestValue().dialogsById[params.id]?.isOpen) {
      this.close(params.id);
    } else {
      this.open(params, closeAll);
    }
  }

  remove = (id: DialogId) => {
    const dialog = this.get(id);
    if (!dialog) return;

    if (dialog.removalTimeout) {
      clearTimeout(dialog.removalTimeout);
    }

    this.state.next((current) => {
      const newDialogs = { ...current.dialogsById };
      delete newDialogs[id];
      return {
        ...current,
        dialogsById: newDialogs,
        openedDialogIds: current.openedDialogIds.filter((dialogId) => dialogId !== id),
      };
    });
  };

  /**
   * Marks the dialog state as unused. If the dialog id is referenced again quickly,
   * the state will not be removed. Otherwise, the state will be removed after
   * a short timeout.
   */
  markForRemoval(id: DialogId) {
    const dialog = this.get(id);

    if (!dialog) {
      return;
    }

    this.state.next((current) => ({
      ...current,
      dialogsById: {
        ...current.dialogsById,
        [id]: {
          ...dialog,
          removalTimeout: setTimeout(() => {
            this.remove(id);
          }, 16),
        },
      },
      openedDialogIds: current.openedDialogIds.filter((dialogId) => dialogId !== id),
    }));
  }

  cancelPendingRemoval(id: DialogId) {
    const dialog = this.get(id);

    if (!dialog?.removalTimeout) {
      return;
    }

    clearTimeout(dialog.removalTimeout);

    this.state.next((current) => ({
      ...current,
      dialogsById: {
        ...current.dialogsById,
        [id]: {
          ...current.dialogsById[id],
          removalTimeout: undefined,
        },
      },
      openedDialogIds: current.dialogsById[id]?.isOpen
        ? [...current.openedDialogIds.filter((dialogId) => dialogId !== id), id]
        : current.openedDialogIds,
    }));
  }
}
